import { h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'

import type { FeedbackSeverity, FeedbackType } from '../types'
import { Annotator } from './Annotator'
import type { StringKey } from './i18n'
import { truncateUrl, validateScreenshotFile } from './screenshot-utils'

export type FormStatus = 'idle' | 'submitting' | 'error' | 'success'

export interface FormValues {
  description: string
  feedback_type: FeedbackType
  severity: FeedbackSeverity
  /** Optional user-attached screenshot — via upload, paste, drag-drop, or
   *  the annotator. v0.12 dropped html2canvas-on-submit; v0.7.1 dropped the
   *  getDisplayMedia "Capture this page" path (asked the operator for screen
   *  share permission on every click, too high a friction for the value). */
  screenshot?: Blob
  /** Always "manual" when a screenshot is attached — kept as a discriminated
   *  field so the backend schema (which still accepts the legacy values) can
   *  evolve independently. Undefined when no screenshot is attached. */
  capture_method?: 'manual'
}

interface FormProps {
  strings: Record<StringKey, string>
  onSubmit: (values: FormValues) => void
  onCancel: () => void
  status: FormStatus
  errorMessage?: string
}

const TYPES: FeedbackType[] = ['bug', 'feature', 'question', 'praise', 'typo']
const SEVERITIES: FeedbackSeverity[] = ['blocker', 'high', 'medium', 'low']

export function Form({ strings, onSubmit, onCancel, status, errorMessage }: FormProps) {
  const [description, setDescription] = useState('')
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug')
  const [severity, setSeverity] = useState<FeedbackSeverity>('medium')
  const [localError, setLocalError] = useState('')
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null)
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [annotatorOpen, setAnnotatorOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const dropZoneRef = useRef<HTMLDivElement | null>(null)

  const submitting = status === 'submitting'
  const submitLabel = submitting ? strings['form.submitting'] : strings['form.submit']
  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''

  // Revoke any object URL when the preview changes or the form unmounts.
  useEffect(() => {
    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
    }
  }, [screenshotPreview])

  const acceptFile = (file: File | Blob) => {
    setLocalError('')
    if (file instanceof File) {
      const err = validateScreenshotFile(file)
      if (err) {
        setLocalError(
          err.kind === 'type'
            ? strings['form.screenshot.error_type']
            : strings['form.screenshot.error_size'].replace('{max}', String(err.maxMb)),
        )
        return
      }
    }
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
    setScreenshotBlob(file)
    setScreenshotPreview(URL.createObjectURL(file))
  }

  const clearScreenshot = () => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
    setScreenshotPreview(null)
    setScreenshotBlob(null)
    setLocalError('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileInputChange = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (file) acceptFile(file)
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.currentTarget === e.target) setIsDragOver(false)
  }
  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) acceptFile(file)
  }

  // Clipboard paste, scoped to the dropzone — so we don't hijack paste from
  // textareas elsewhere in the page or in our own description field.
  useEffect(() => {
    const zone = dropZoneRef.current
    if (!zone) return
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) {
            e.preventDefault()
            acceptFile(file)
            return
          }
        }
      }
    }
    zone.addEventListener('paste', onPaste)
    return () => zone.removeEventListener('paste', onPaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshotPreview])

  const handleAnnotated = (annotated: Blob) => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
    setScreenshotBlob(annotated)
    setScreenshotPreview(URL.createObjectURL(annotated))
    // Annotation preserves whatever method seeded the source — a
    // display_media capture that gets annotated stays display_media.
    setAnnotatorOpen(false)
  }

  const handleSubmit = (e: Event) => {
    e.preventDefault()
    if (!description.trim()) {
      setLocalError(strings['form.description.required'])
      return
    }
    setLocalError('')
    const values: FormValues = {
      description: description.trim(),
      feedback_type: feedbackType,
      severity,
    }
    if (screenshotBlob) {
      values.screenshot = screenshotBlob
      values.capture_method = 'manual'
    }
    onSubmit(values)
  }

  return (
    <form onSubmit={handleSubmit}>
      <h2>{strings['form.title']}</h2>

      <div class="field">
        <label for="mfb-desc">{strings['form.description.label']}</label>
        <textarea
          id="mfb-desc"
          value={description}
          placeholder={strings['form.description.placeholder']}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
        />
      </div>

      <div class="row">
        <div class="field">
          <label for="mfb-type">{strings['form.type.label']}</label>
          <select
            id="mfb-type"
            value={feedbackType}
            onChange={(e) => setFeedbackType((e.target as HTMLSelectElement).value as FeedbackType)}
          >
            {TYPES.map((t) => <option value={t}>{strings[`type.${t}` as StringKey]}</option>)}
          </select>
        </div>
        <div class="field">
          <label for="mfb-sev">{strings['form.severity.label']}</label>
          <select
            id="mfb-sev"
            value={severity}
            onChange={(e) => setSeverity((e.target as HTMLSelectElement).value as FeedbackSeverity)}
          >
            {SEVERITIES.map((s) => <option value={s}>{strings[`severity.${s}` as StringKey]}</option>)}
          </select>
        </div>
      </div>

      <div class="field">
        <label>{strings['form.screenshot.label']}</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          class="mfb-sr-only"
          onChange={handleFileInputChange}
          aria-hidden="true"
          tabIndex={-1}
        />
        {screenshotPreview ? (
          <div class="screenshot-preview">
            <img src={screenshotPreview} alt="" />
            <div class="screenshot-preview-actions">
              <button
                type="button"
                class="btn btn--primary screenshot-annotate"
                onClick={() => setAnnotatorOpen(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                {strings['form.screenshot.annotate']}
              </button>
              <button type="button" class="btn" onClick={clearScreenshot}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/></svg>
                {strings['form.screenshot.remove']}
              </button>
            </div>
          </div>
        ) : (
          <div
            ref={dropZoneRef}
            class={`screenshot-dropzone ${isDragOver ? 'is-dragover' : ''}`}
            tabIndex={0}
            role="button"
            aria-label={strings['form.screenshot.label']}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                fileInputRef.current?.click()
              }
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <svg class="screenshot-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <div class="screenshot-cta">
              <strong>{strings['form.screenshot.cta_click']}</strong>
              {', '}
              {strings['form.screenshot.cta_rest']}
            </div>
            <div class="screenshot-formats">{strings['form.screenshot.formats']}</div>
          </div>
        )}
      </div>

      {pageUrl && (
        <div class="page-context" title={pageUrl}>
          <span class="page-context-label">{strings['form.context.label']}</span>
          <span class="page-context-url">{truncateUrl(pageUrl, 90)}</span>
        </div>
      )}

      {localError && <div class="error">{localError}</div>}
      {status === 'error' && errorMessage && <div class="error">{errorMessage}</div>}
      {status === 'success' && <div class="success">{strings['form.success']}</div>}

      <div class="actions">
        <button type="button" class="btn" onClick={onCancel} disabled={submitting}>{strings['form.cancel']}</button>
        <button type="submit" class="btn btn--primary" disabled={submitting}>{submitLabel}</button>
      </div>

      {annotatorOpen && screenshotBlob && (
        <Annotator
          imageBlob={screenshotBlob}
          strings={strings}
          onCancel={() => setAnnotatorOpen(false)}
          onSave={handleAnnotated}
        />
      )}
    </form>
  )
}
